import { useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'

import VoucherDisplayModel from 'marketing/components/VoucherDisplayModel'
import { INTL_TIME_DAY_MONTH_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import { useLocaleUrlOptions } from 'hooks/useLocaleUrlOptions'
import { getVoucherCallToAction, isAbsoluteVoucherCallToActionUrl } from 'lib/voucher/getVoucherCallToAction'
import { buildUrl } from 'lib/url/searchUrlUtils'

const VOUCHERS_PAGE_PATH = '/account/vouchers'

interface Props {
  voucher: App.PublicVoucher
  /** Called when the reminder opens, so it is not shown again. */
  onShow: () => void
  /** Called when the customer closes the reminder. */
  onClose: () => void
}

/**
 * Reminds a logged-in customer that one of their vouchers expires within 48 hours (GR-5151).
 */
function ExpiringVoucherPrompt({ voucher, onShow, onClose }: Props) {
  const intl = useIntl()
  const promptRef = useRef<HTMLDivElement | null>(null)
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const { locale, languageOptions } = useLocaleUrlOptions()

  useEffect(() => {
    onShow()
  }, [onShow])

  const title = intl.formatMessage({
    id: 'bWFRqrQfm4Od1MxWkjnA',
    defaultMessage: 'Your voucher expires soon',
    description: 'Heading of the popup reminding a logged-in customer that one of their vouchers expires within 48 hours',
  })

  const body = intl.formatMessage({
    id: '_5SeXw1JJyDc9i14Il9h',
    defaultMessage: 'Book before it expires to make the most of it. It applies automatically at checkout for eligible bookings.',
    description: 'Body text of the popup reminding a logged-in customer to use their voucher before it expires within 48 hours',
  })

  const viewVouchersText = intl.formatMessage({
    id: 'QPXGzl3_zDMgxcnbHWnS',
    defaultMessage: 'View my vouchers',
    description: 'Button in the expiring voucher reminder popup that opens the account vouchers page when the voucher has no call to action',
  })

  // Use the voucher's own call to action when it has one, else send the customer to their vouchers.
  const callToAction = useMemo(
    () => getVoucherCallToAction(voucher) ?? { text: viewVouchersText, url: VOUCHERS_PAGE_PATH },
    [voucher, viewVouchersText],
  )
  // Absolute URLs render as a plain anchor (href); internal paths get regionalised and
  // render as an in-app link (to).
  const isExternalCta = isAbsoluteVoucherCallToActionUrl(callToAction.url)
  const ctaHref = isExternalCta ? callToAction.url : undefined
  const ctaTo = isExternalCta
    ? undefined
    : buildUrl(callToAction.url, region, undefined, undefined, { locale, languageOptions })

  const expiryText = useMemo(() => {
    if (!voucher.expiresAt) return undefined
    return intl.formatMessage(
      {
        id: 'mpuJpdNv7B2vus-sH2IQ',
        defaultMessage: 'Expires {expiryDateTime}',
        description: 'Expiry date and time on the voucher card in the popup reminding a customer their voucher expires within 48 hours',
      },
      {
        expiryDateTime: intlDateFormatter(new Date(voucher.expiresAt), INTL_TIME_DAY_MONTH_FORMAT, intl.locale),
      },
    )
  }, [intl, voucher.expiresAt])

  return (
    <VoucherDisplayModel
      show
      promptRef={promptRef}
      voucher={voucher}
      title={title}
      body={body}
      // The reminder only shows once, so "do not show again" would do nothing.
      displayMode="persistence"
      expiryText={expiryText}
      onClose={onClose}
      onDismiss={onClose}
      ctaText={callToAction.text}
      ctaTo={ctaTo}
      ctaHref={ctaHref}
      onCtaClick={onClose}
    />
  )
}

export default ExpiringVoucherPrompt
